import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { withTheme } from 'styled-components';
import { dateFormat } from 'highcharts';
import { Box, Button, Flex, Highcharts, Suspense, Tag, Text } from 'core/components';
import {
  OPERATOR_SHORT_LABELS,
  ALERT_UI_STATES,
  POLICY_APPLICATIONS,
  NOTIFY_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_SEVERITIES
} from '@kentik/ui-shared/alerting/constants';
import WidgetFrame from 'app/components/decks/widgets/WidgetFrame';
import { VirtualizedTable } from 'core/components/table';
import { timezone } from 'core/util/dateUtils';
import TargetRenderer from 'app/views/protect/ddos/analyze/components/TargetRenderer';
import MetricRenderer from 'app/views/protect/ddos/analyze/components/MetricRenderer';
import NmsNativeAlertMetricListRenderer from 'app/views/alerting/components/nms/NmsNativeAlertMetricListRenderer';
import NmsNativeAlertDimensionRenderer from 'app/views/alerting/components/nms/NmsNativeAlertDimensionRenderer';
import AlertCollection from 'app/stores/alerting/AlertCollection';
import { Field, FormComponent, Select } from 'core/form';

import { DEFAULT_QUALITATIVE_PALETTES } from 'app/stores/colors/Colors';
import LoadingSkeleton from 'app/views/metrics/LoadingSkeleton';
import AlertStatusTag from 'app/views/alerting/components/AlertStatusTag';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import withConfigOptions from '../withConfigOptions';

const GROUP_BY = {
  SEVERITY: 'severity',
  POLICY: 'policyName'
};

function getDefaultAlertFetchOptions() {
  return {
    states: [ALERT_UI_STATES.ACTIVE, ALERT_UI_STATES.CLEAR],
    applications: [POLICY_APPLICATIONS.NMS],
    started: {
      start: timezone.momentFn().subtract(6, 'days').startOf('day').toISOString(),
      end: timezone.momentFn().toISOString()
    },
    lookback: 0,
    sorting: {
      fields: [
        {
          name: 'state',
          order: 'SORT_ORDER_ASCENDING'
        },
        {
          name: 'start_time',
          order: 'SORT_ORDER_DESCENDING'
        }
      ]
    }
  };
}

@inject('$alerting')
@withConfigOptions
@withRouter
@withTheme
@observer
class AlertingHistoryWidget extends Component {
  static defaultProps = {
    contentRef: React.createRef()
  };

  static fields = {
    groupBy: {
      label: 'Visualization Grouping',
      helpText: 'Choose the grouping for the visualization.',
      defaultValue: GROUP_BY.POLICY,
      options: [
        {
          label: 'Policy Name',
          value: GROUP_BY.POLICY
        },
        {
          label: 'Severity',
          value: GROUP_BY.SEVERITY
        }
      ]
    }
  };

  state = {
    loading: true,
    hasLoaded: false,
    statsLoaded: false,
    alertFilters: getDefaultAlertFetchOptions(),
    alertCollection: new AlertCollection(),
    statsData: null,
    statsCounts: { active: 0, cleared: 0, ackRequired: 0 },
    isChartFiltered: false,
    activeFilterLabel: null
  };

  componentDidMount() {
    this.fetchChartData();
    this.fetchTableData();
  }

  componentDidUpdate(prevProps) {
    const prevGroupBy = prevProps.config?.groupBy;
    const { config } = this.props;
    if (prevGroupBy !== config?.groupBy) {
      this.setState({ statsData: null }, () => this.fetchChartData());
    }
  }

  componentWillUnmount() {
    this.cancelTableFetch?.();
    if (this.resizeWatcher && this.el) {
      this.resizeWatcher.unobserve(this.el);
    } else if (this.resizeWatcher) {
      this.resizeWatcher.disconnect();
    }
  }

  get config() {
    const { config } = this.props;
    const { groupBy = GROUP_BY.POLICY } = config;
    return { groupBy };
  }

  get isBySeverity() {
    return this.config.groupBy === GROUP_BY.SEVERITY;
  }

  fetchChartData() {
    const { $alerting } = this.props;
    const start = timezone.momentFn().subtract(6, 'days').startOf('day').toISOString();
    const end = timezone.momentFn().toISOString();
    const group_by = {
      state: true,
      ack_state: true,
      severity: this.isBySeverity,
      rule_id: !this.isBySeverity
    };

    return $alerting
      .getAlarmStatsData({
        filter: {
          started: { start, end },
          applications: [POLICY_APPLICATIONS.NMS]
        },
        group_by,
        bucket: 'day'
      })
      .then((statsData) => {
        const stats = statsData?.stats ?? [];
        const statsCounts = { active: 0, cleared: 0, ackRequired: 0 };
        stats.forEach((stat) => {
          const statCountNum = Number(stat?.count || '0');
          if (stat.state === 'active') {
            statsCounts.active += statCountNum;
          } else {
            statsCounts.cleared += statCountNum;
          }

          if (stat.ack_state === 'ack_required') {
            statsCounts.ackRequired += statCountNum;
          }
        });

        this.setState({ statsData, statsCounts, statsLoaded: true });
      })
      .catch((err) => {
        this.setState({ statsLoaded: true });
        console.error('fetchChartData error', err);
      });
  }

  fetchTableData() {
    this.cancelTableFetch?.();
    this.setState({ loading: true });

    const { alertFilters, alertCollection, isChartFiltered } = this.state;
    const { promise, cancel } = makeCancelable(
      alertCollection.setServerFilter(
        alertFilters,
        {},
        { data: { limit: 100, offset: 0, pagination: { includeTotalCount: isChartFiltered } } }
      )
    );

    this.cancelTableFetch = cancel;

    return promise
      .then(() => {
        this.setState({ loading: false, hasLoaded: true });
      })
      .catch((err) => {
        if (!(err instanceof CanceledError)) {
          console.error('fetchTableData error', err);
        }
      });
  }

  handleSave(form) {
    const { handleSaveConfiguration } = this.props;
    handleSaveConfiguration(form);
  }

  buildChartSeriesFromStats() {
    const { statsData } = this.state;
    const { $alerting, theme } = this.props;
    const stats = statsData?.stats ?? [];

    const seriesMap = {};
    stats.forEach((stat) => {
      stat.severity =
        NOTIFY_SEVERITY_TO_STANDARD_ALERT_SEVERITY[stat.severity] ||
        ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[stat.severity] ||
        stat.severity;
      const key = this.isBySeverity ? stat.severity : stat.rule_id;

      if (!key || !stat.bucket_start_time) {
        return;
      }

      if (!seriesMap[key]) {
        const name = this.isBySeverity
          ? stat.severity
          : $alerting.policyCollection.getPolicyByRuleId(key)?.get('name') || key;
        seriesMap[key] = { name, buckets: {} };
      }

      seriesMap[key].buckets[stat.bucket_start_time] =
        (seriesMap[key].buckets[stat.bucket_start_time] || 0) + stat.count;
    });

    // When grouping by severity, order series by ALERT_SEVERITIES rank so that
    // the colors array passed to <Highcharts> aligns with the correct CSS color index.
    const orderedKeys = this.isBySeverity ? ALERT_SEVERITIES.filter((sev) => seriesMap[sev]) : Object.keys(seriesMap);

    const series = orderedKeys.map((key) => ({
      name: seriesMap[key].name,
      data: Object.keys(seriesMap[key].buckets)
        .sort()
        .map((dayStr) => ({
          x: timezone.momentFn(dayStr).valueOf(),
          y: seriesMap[key].buckets[dayStr],
          name: seriesMap[key].name,
          ruleId: this.isBySeverity ? undefined : key,
          severity: this.isBySeverity ? key : undefined,
          dayStart: dayStr
        }))
    }));

    const colors = this.isBySeverity
      ? orderedKeys.map((key) => theme.colors.severity[key])
      : DEFAULT_QUALITATIVE_PALETTES.qualitative4;

    const allX = series.flatMap(({ data }) => data.map(({ x }) => x));

    return {
      series,
      colors,
      minX: allX.length ? Math.min(...allX) : undefined,
      maxX: allX.length ? Math.max(...allX) : undefined
    };
  }

  generateChartConfig = ({ series, minX, maxX }) => {
    const { $alerting } = this.props;
    const { isBySeverity } = this;
    const tickMargin = 12 * 3600 * 1000; // 12 hours in milliseconds

    return {
      series,
      chart: {
        type: 'column'
      },

      title: {
        text: ''
      },

      xAxis: {
        type: 'datetime',
        min: minX ? minX - tickMargin : undefined,
        max: maxX ? maxX + tickMargin : undefined,
        tickInterval: tickMargin * 2, // 1 day in milliseconds
        showFirstLabel: false,
        showLastLabel: false,
        labels: {
          formatter() {
            return dateFormat('%e %b', this.value);
          }
        },
        title: {
          text: null
        }
      },
      legend: {
        enabled: false
      },
      yAxis: {
        maxPadding: 0.1,
        title: {
          text: 'Alert Count'
        },
        stackLabels: {
          align: 'center',
          textAlign: 'center',
          enabled: true,
          formatter() {
            return `<b>${this.total?.toLocaleString()}</b>`;
          }
        }
      },

      tooltip: {
        formatter() {
          const label = isBySeverity ? $alerting.getSeverityLabel(this.series.name) : this.series.name;
          return `${label}: ${this.y?.toLocaleString() || '0'}`;
        }
      },

      plotOptions: {
        column: {
          stacking: 'normal'
        },
        series: {
          allowPointSelect: true,
          groupPadding: 0.1,
          point: {
            events: {
              select: this.handleSelectSeries,
              unselect: this.handleUnselectSeries
            }
          }
        }
      }
    };
  };

  handleSetAlertFilters = (filters) => {
    const { $alerting } = this.props;
    const { ruleId, severity, dayStart, dayEnd } = filters;
    if (!ruleId && !severity) {
      return;
    }
    const newAlertFilters = getDefaultAlertFetchOptions();

    if (ruleId || severity) {
      // Chart click: show active + cleared for that policy/severity over the selected day
      if (ruleId) {
        newAlertFilters.ruleIds = [ruleId];
      }
      if (severity) {
        newAlertFilters.severities = [severity];
      }
      if (dayStart && dayEnd) {
        newAlertFilters.started = { start: dayStart, end: dayEnd };
        newAlertFilters.active = null;
        newAlertFilters.lookback = null;
      }
    }

    const name = ruleId
      ? `"${$alerting.policyCollection.getPolicyByRuleId(ruleId)?.policyName || ruleId}"`
      : $alerting.getSeverityLabel(severity);
    const day = dayStart ? timezone.momentFn(dayStart).utc().format('D MMM') : null;
    const activeFilterLabel = day ? `${name} alerts, started ${day}` : `${name}`;

    this.setState({ alertFilters: newAlertFilters, isChartFiltered: true, activeFilterLabel }, () =>
      this.fetchTableData()
    );
  };

  handleResetAlertFilters = () => {
    this.setState(
      { alertFilters: getDefaultAlertFetchOptions(), isChartFiltered: false, activeFilterLabel: null },
      () => {
        this.fetchTableData();
      }
    );
  };

  chartCallback = (chart) => {
    this.chart = chart.container ? chart : null;
    this.el = chart.container;
    this.resizeWatcher = new ResizeObserver(() => {
      try {
        this.chart.reflow();
      } catch (e) {
        // OK to ignore
      }
    });
    this.resizeWatcher.observe(this.el);
  };

  // when a Series is selected, filter the Table below to only include those Alerts
  handleSelectSeries = (e) => {
    const { ruleId, severity, dayStart } = e.target;
    const dayEnd = timezone.momentFn(dayStart).add(1, 'day').toISOString();
    this.handleSetAlertFilters({ ruleId, severity, dayStart, dayEnd });
  };

  handleUnselectSeries = () => {
    // Defer check so the incoming select event fires first before we decide to reset
    setTimeout(() => {
      if (!this.chart?.getSelectedPoints?.()?.length) {
        this.handleResetAlertFilters();
      }
    }, 0);
  };

  renderConfigurePanel() {
    const { handleCancelConfiguration, model, config } = this.props;
    const { fields } = AlertingHistoryWidget;

    return (
      <FormComponent fields={fields} model={model} options={{ name: 'Configure Alert History', large: true }}>
        {({ form }) => (
          <Flex p={2} gap={3}>
            <Box>
              <Field name="groupBy" large>
                <Select />
              </Field>

              <Flex gap={1} mt={3}>
                {config.lookback !== undefined && config.interval !== undefined && (
                  <Button text="Cancel" flex={1} minWidth={100} onClick={() => handleCancelConfiguration(form)} />
                )}
                <Button
                  text="Save"
                  flex={1}
                  intent="primary"
                  disabled={!form.valid}
                  onClick={() => this.handleSave(form)}
                />
              </Flex>
            </Box>
          </Flex>
        )}
      </FormComponent>
    );
  }

  renderWidgetContent() {
    const { history, height } = this.props;
    const { loading, hasLoaded, statsLoaded, alertCollection, statsCounts, isChartFiltered, activeFilterLabel } =
      this.state;
    const { active, cleared } = statsCounts;

    const columns = [
      {
        label: 'Status',
        name: 'status',
        id: 'status',
        value: 'status',
        computed: true,
        width: 105,
        ellipsis: false,
        renderer: ({ model }) => <AlertStatusTag isActive={model.isActive} />
      },
      {
        label: 'Policy',
        name: 'policyName',
        id: 'policyName',
        value: 'policyName',
        computed: true,
        flexBasis: 120,
        minWidth: 120
      },
      {
        label: 'Dimensions',
        name: 'primaryDimension',
        id: 'primaryDimension',
        value: 'primaryDimension',
        flexBasis: 180,
        minWidth: 180,
        computed: true,
        ellipsis: false,
        renderer: ({ model }) =>
          model.isNmsPolicy ? (
            <NmsNativeAlertDimensionRenderer maxDimensionsToShow={3} alertModel={model} />
          ) : (
            <TargetRenderer model={model} />
          )
      },
      {
        label: 'Threshold Metric',
        name: 'metric',
        id: 'metric',
        value: 'metric',
        sortable: false,
        ellipsis: false,
        minWidth: 100,
        renderer: ({ model }) => {
          let operator = '';
          if (model.threshold?.conditions?.length > 0) {
            operator = model.threshold.conditions[0].operator;
          }
          const operatorLabel = OPERATOR_SHORT_LABELS[operator];
          return (
            <Flex gap="4px" alignItems="center">
              {!model.isNmsPolicy && operatorLabel !== '=' && <Text>{operatorLabel}</Text>}
              {!model.isNmsPolicy && <MetricRenderer model={model} showLabel={false} />}
              {model.isNmsPolicy && <NmsNativeAlertMetricListRenderer alertModel={model} numMetricsToShow={2} />}
            </Flex>
          );
        }
      },
      {
        label: 'Duration',
        name: 'displayDuration',
        id: 'displayDuration',
        value: 'displayDuration',
        computed: true,
        sortable: true,
        width: 170,
        minWidth: 120
      }
    ];

    function handleRowClick(model) {
      history.push(model.alertDetailLink);
    }

    const { totalCount } = alertCollection.pageState;
    const displayTotal = isChartFiltered ? totalCount : active + cleared;

    const { series: chartSeries, colors: chartColors, minX, maxX } = this.buildChartSeriesFromStats();
    const chartOptions = this.generateChartConfig({ series: chartSeries, minX, maxX });

    return (
      <Flex flexDirection="column" flex={1} height="100%">
        <Flex flexDirection="column" flex={1} height="100%">
          <Box height={height / 3}>
            {statsLoaded && (
              <Highcharts
                key={this.config.groupBy}
                height={height / 3}
                width="100%"
                allowChartUpdate
                callback={this.chartCallback}
                colors={chartColors}
                options={chartOptions}
              />
            )}
            {!statsLoaded && <LoadingSkeleton height={height / 3} />}
          </Box>
          <Flex flexDirection="column" flex={1} height="100%">
            {!hasLoaded && <LoadingSkeleton />}
            <VirtualizedTable
              flexed
              collection={alertCollection}
              columns={columns}
              onRowClick={(model) => handleRowClick(model)}
            />

            <Flex justifyContent="space-between" p={1} borderTop="thin">
              <Text small muted>
                Filter: {activeFilterLabel ?? 'NMS alerts started in the last 7 days'}
              </Text>
              <Text small muted>
                {loading && 'Loading...'}
                {!loading && displayTotal > 100 && `Showing 100 of ${displayTotal.toLocaleString()} alerts`}
                {!loading && displayTotal <= 100 && `${displayTotal} alerts`}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  render() {
    const { canCustomize, onRemove, title, handleShowConfigurePanel, isConfigurePanelOpen, ...props } = this.props;
    const { hasLoaded, statsLoaded, statsCounts } = this.state;
    const { active, cleared, ackRequired } = statsCounts;

    return (
      <WidgetFrame
        canCustomize={canCustomize}
        configAction={handleShowConfigurePanel}
        onRemove={onRemove}
        title={
          isConfigurePanelOpen ? (
            'Configure'
          ) : (
            <Flex alignItems="center" gap={1}>
              {title ?? 'Alert History'}
              {statsLoaded && (
                <>
                  <Tag small fontWeight="normal">
                    Last 7 days
                  </Tag>
                  <Tag small intent="danger" fontWeight="normal">
                    {(active || '0').toLocaleString()} Active
                  </Tag>
                  <Tag small fontWeight="normal">
                    {(cleared || '0').toLocaleString()} Cleared
                  </Tag>
                  <Tag small intent="primary" fontWeight="normal">
                    {(ackRequired || '0').toLocaleString()} Ack Required
                  </Tag>
                </>
              )}
            </Flex>
          )
        }
        {...props}
      >
        <Suspense loading={!statsLoaded && !hasLoaded} fallback={<LoadingSkeleton />}>
          {isConfigurePanelOpen && this.renderConfigurePanel()}
          {!isConfigurePanelOpen && this.renderWidgetContent()}
        </Suspense>
      </WidgetFrame>
    );
  }
}

export default AlertingHistoryWidget;
